import React, { useState, lazy, Suspense, useEffect } from 'react';
import {
  UnorderedListOutlined,
  BarChartOutlined,
  ScheduleOutlined,
  UserOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import BookingsTab from '../components/provider/BookingsTab';
import AnalyticsTab from '../components/provider/AnalyticsTab';
import ScheduleTab from '../components/provider/ScheduleTab';
import ProfileTab from '../components/provider/ProfileTab';
import BlockedScreen from '../components/provider/BlockedScreen';
import { useBookingsStore } from '../store/bookingsStore';
import { useAuthStore } from '../store/authStore';
import { providerMeApi } from '../api/providerMeApi';
import { PageLoader } from '../components/shared/PageLoader';

const CreateWalkInBookingPage = lazy(
  () => import('./provider/CreateWalkInBookingPage')
);

type TabKey = 'bookings' | 'analytics' | 'schedule' | 'profile';

const NAV_ITEMS: { key: TabKey; icon: React.ReactNode; labelKey: string }[] = [
  { key: 'bookings',  icon: <UnorderedListOutlined />, labelKey: 'navigation.bookings'  },
  { key: 'analytics', icon: <BarChartOutlined />,      labelKey: 'navigation.analytics' },
  { key: 'schedule',  icon: <ScheduleOutlined />,      labelKey: 'navigation.schedule'  },
  { key: 'profile',   icon: <UserOutlined />,          labelKey: 'navigation.profile'   },
];

function StaffDashboardPage() {
  const { t } = useTranslation('provider');
  const [activeTab, setActiveTab] = useState<TabKey>('bookings');
  // Lazy mount: tab birinchi marta tanlangach mount bo'ladi, keyin saqlanib qoladi.
  // Boshlanishda faqat default tab (bookings) mount qilinadi — qolgan 3 ta tabning
  // API chaqiruvlari faqat foydalanuvchi shu tablarga o'tganda ishga tushadi.
  const [mountedTabs, setMountedTabs] = useState<Set<TabKey>>(() => new Set(['bookings']));
  const [isCreateFlowOpen, setIsCreateFlowOpen] = useState<boolean>(false);
  const [blockedState, setBlockedState] = useState<'loading' | 'blocked' | 'allowed'>('loading');

  const refresh = useBookingsStore((s) => s.refresh);
  const logout = useAuthStore((s) => s.logout);

  const switchTab = (key: TabKey) => {
    setActiveTab(key);
    setMountedTabs((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleLogout = () => {
    void logout();
  };

  useEffect(() => {
    let cancelled = false;

    providerMeApi.getProfile()
      .then((profile) => {
        if (!cancelled) {
          setBlockedState(profile.enabledByOwner ? 'allowed' : 'blocked');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBlockedState('allowed');
        }
      });

    const handleBlocked = () => {
      if (!cancelled) setBlockedState('blocked');
    };
    window.addEventListener('provider:blocked', handleBlocked);

    return () => {
      cancelled = true;
      window.removeEventListener('provider:blocked', handleBlocked);
    };
  }, []);

  // Dashboard mount bo'lganda chunkni fonda yuklaydi — FAB bosilganda kechikish bo'lmaydi
  useEffect(() => {
    void import('./provider/CreateWalkInBookingPage');
  }, []);

  if (blockedState === 'loading') {
    return <PageLoader />;
  }

  if (blockedState === 'blocked') {
    return <BlockedScreen onLogout={handleLogout} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bl-surface-soft, #f7f8fa)' }}>

      {/* ─── Tab content (lazy mount) ────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {mountedTabs.has('bookings') && (
          <div style={{ display: activeTab === 'bookings' ? 'flex' : 'none', height: '100%', flexDirection: 'column' }}>
            <BookingsTab />
          </div>
        )}
        {mountedTabs.has('analytics') && (
          <div style={{ display: activeTab === 'analytics' ? 'block' : 'none', height: '100%', overflow: 'hidden' }}>
            <AnalyticsTab />
          </div>
        )}
        {mountedTabs.has('schedule') && (
          <div style={{ display: activeTab === 'schedule' ? 'block' : 'none', height: '100%', overflow: 'hidden' }}>
            <ScheduleTab />
          </div>
        )}
        {mountedTabs.has('profile') && (
          <div style={{ display: activeTab === 'profile' ? 'block' : 'none', height: '100%', overflow: 'hidden' }}>
            <ProfileTab />
          </div>
        )}
      </div>

      {/* ─── FAB — Walk-in bron yaratish ────────────────────────── */}
      {!isCreateFlowOpen && (
        <button
          onClick={() => setIsCreateFlowOpen(true)}
          style={{
            position: 'fixed',
            bottom: '60px',
            right: '16px',
            zIndex: 100,
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'var(--bl-accent, #3390ec)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            boxShadow: '0 4px 16px rgba(51,144,236,0.45)',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <PlusOutlined />
        </button>
      )}

      {/* ─── CreateWalkIn overlay ────────────────────────────────── */}
      {isCreateFlowOpen && (
        <Suspense fallback={null}>
          <CreateWalkInBookingPage
            onClose={() => {
              setIsCreateFlowOpen(false);
              void refresh();
            }}
          />
        </Suspense>
      )}

      {/* ─── Bottom nav bar ──────────────────────────────────────── */}
      <nav style={{
        flexShrink: 0,
        background: 'var(--bl-surface, #fff)',
        borderTop: '1px solid var(--bl-divider, #ebebeb)',
        display: 'flex',
        alignItems: 'stretch',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {NAV_ITEMS.map(({ key, icon, labelKey }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => switchTab(key)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                padding: '10px 4px 8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {/* Active indicator */}
              {active && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 24,
                  height: 3,
                  borderRadius: '0 0 3px 3px',
                  background: 'var(--bl-accent, #3390ec)',
                }} />
              )}

              {/* Icon */}
              <div style={{
                fontSize: 22,
                lineHeight: 1,
                color: active ? 'var(--bl-accent, #3390ec)' : 'var(--bl-text-muted, #9ca3af)',
                transition: 'color 0.15s',
              }}>
                {icon}
              </div>

              {/* Label */}
              <span style={{
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                color: active ? 'var(--bl-accent, #3390ec)' : 'var(--bl-text-muted, #9ca3af)',
                letterSpacing: active ? -0.2 : 0,
                transition: 'color 0.15s, font-weight 0.15s',
              }}>
                {t(labelKey)}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default StaffDashboardPage;
